import { useState } from "react";
import { newIdempotencyKey } from "@teamna/web-client/api/client.ts";
import { useRemoveProfilePhoto, useSaveProfilePhotos } from "@teamna/web-client/hooks/use-photos.ts";
import { showToast } from "../components/index.ts";
import { SinglePhotoEditor } from "../photos/SinglePhotoEditor.tsx";

/**
 * The owner adds, replaces or removes the profile photo of one participant (the member profile).
 * The same save as the Team photo tool, with one photo, so the family is told the same way.
 */
export function OwnerParticipantPhoto({
  businessId,
  participantId,
  name,
  photoUrl,
  photosRefused,
  onDone,
}: {
  businessId: string;
  participantId: string;
  name: string;
  photoUrl: string | null;
  photosRefused: boolean;
  onDone?: () => void;
}) {
  const save = useSaveProfilePhotos(businessId);
  // One key per crop sent: the same crop sent again (a retry) replays, a new crop is a new save.
  const [sent, setSent] = useState<{ jpeg: string; key: string } | null>(null);
  const keyFor = (jpeg: string): string => {
    if (sent?.jpeg === jpeg) return sent.key;
    const key = newIdempotencyKey();
    setSent({ jpeg, key });
    return key;
  };
  const remove = useRemoveProfilePhoto(businessId);
  return (
    <SinglePhotoEditor
      name={name}
      photoUrl={photoUrl}
      saving={save.isPending}
      removing={remove.isPending}
      error={save.error ?? remove.error}
      {...(photosRefused ? { blockedMessage: "The family does not allow a profile photo. Only the family can change this." } : {})}
      onSave={(jpeg, done) =>
        save.mutate(
          { photos: [{ subject: { kind: "participant", id: participantId }, jpeg }], idempotencyKey: keyFor(jpeg) },
          {
            onSuccess: () => {
              done();
              showToast("The photo is saved. The family got a message.");
              onDone?.();
            },
          },
        )
      }
      onRemove={() =>
        remove.mutate(
          { kind: "participant", id: participantId },
          {
            onSuccess: () => {
              showToast("The photo is removed.");
              onDone?.();
            },
          },
        )
      }
    />
  );
}
